// src/features/deadlines/components/ScheduledFinalizationEmails.tsx
//
// Stufe 3: Anzeige der eingefrorenen Finalisieren-Mandantenmails im
// Fristen-Dashboard. Bewusst temporär: Solange keine geplante oder
// fehlgeschlagene Mail existiert, rendert die Komponente NICHTS —
// null Platzverbrauch im Normalzustand.
//
// Darstellung: kompakte Kopfzeile pro Mail (Betreff, Empfänger,
// Versandzeitpunkt). Die Detailansicht mit Label-Block, vollständigem
// eingefrorenem Mail-Inhalt und Anhängen wird per Klick aufgeklappt.
//
// Wording und Aufbau des Label-Blocks folgen dem etablierten Ticket-Banner:
//   An / Zeitpunkt / Von (Innenidentität) / Senden als (Außenidentität),
// darunter der vollständige eingefrorene Mail-Inhalt (exakt so, wie der
// Cron ihn versendet) und die Anhänge mit Download.
//
// Abbrechen (nur die Mail — F1: der Monat bleibt finalisiert!):
// bedingtes Delete mit Claim-Guard gegen den Minuten-Cron; verliert das
// Frontend das Rennen, kommt eine verständliche Meldung. Fehlgeschlagene
// Mails können gelöscht werden (Neuversand läuft manuell).

import React, { useState, useEffect } from 'react';
import { Clock, AlertCircle, X, Trash2, Download, ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../../../shared/services/supabase';
import { formatBerlinDateTime } from '../../../shared/utils/berlinTime';

interface ScheduledEmailAttachmentMeta {
  filename: string;
  content_type: string;
  storage_path: string;
  size_bytes: number;
}

interface ScheduledFinalizationEmail {
  id: string;
  customer_id: string | null;
  monat: number;
  jahr: number;
  to_email: string;
  cc_email: string | null;
  subject: string;
  body_html: string;
  attachments: ScheduledEmailAttachmentMeta[];
  sender_identity_name: string | null;
  sender_identity_email: string | null;
  erstellt_von_name: string;
  versand_status: 'geplant' | 'versendet' | 'fehler';
  geplanter_versand_am: string;
  fehler_nachricht: string | null;
}

const ATTACHMENT_BUCKET = 'scheduled-email-attachments';

export const ScheduledFinalizationEmails: React.FC = () => {
  const [emails, setEmails] = useState<ScheduledFinalizationEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [downloadingPaths, setDownloadingPaths] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;

    const loadEmails = async () => {
      try {
        // Explizites .limit() wegen Supabase-Default-Row-Limit von 1000
        const { data, error: loadError } = await supabase
          .from('scheduled_finalization_emails')
          .select('id, customer_id, monat, jahr, to_email, cc_email, subject, body_html, attachments, sender_identity_name, sender_identity_email, erstellt_von_name, versand_status, geplanter_versand_am, fehler_nachricht')
          .in('versand_status', ['geplant', 'fehler'])
          .order('geplanter_versand_am', { ascending: true })
          .limit(100000);

        if (cancelled) return;
        if (loadError) {
          console.error('[ScheduledFinalizationEmails] Laden fehlgeschlagen:', loadError.message);
          return;
        }
        setEmails((data || []) as ScheduledFinalizationEmail[]);
      } catch (err) {
        if (!cancelled) {
          console.error('[ScheduledFinalizationEmails] Laden fehlgeschlagen:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadEmails();

    return () => {
      cancelled = true;
    };
  }, []);

  // Temporär per Anforderung: nichts blockieren, solange nichts geplant ist.
  // Auch während des initialen Ladens wird nichts gerendert (kein Flackern).
  if (loading || emails.length === 0) return null;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDownload = async (meta: ScheduledEmailAttachmentMeta) => {
    setDownloadingPaths(prev => new Set(prev).add(meta.storage_path));
    setError(null);

    try {
      const { data, error: urlError } = await supabase.storage
        .from(ATTACHMENT_BUCKET)
        .createSignedUrl(meta.storage_path, 3600, { download: meta.filename });

      if (urlError || !data) {
        setError(`Download-Link konnte nicht erstellt werden (${meta.filename}).`);
        return;
      }

      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = meta.filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('[ScheduledFinalizationEmails] Download fehlgeschlagen:', err);
      setError(`Download fehlgeschlagen (${meta.filename}).`);
    } finally {
      setDownloadingPaths(prev => {
        const next = new Set(prev);
        next.delete(meta.storage_path);
        return next;
      });
    }
  };

  // Abbrechen (geplant) bzw. Löschen (fehler). Reihenfolge bewusst:
  // ZUERST das bedingte Zeilen-Delete (atomarer Guard gegen den Cron),
  // DANACH der Storage-Cleanup (best-effort — verwaiste Dateien sind
  // harmlos; umgekehrt wäre eine geclaimte Mail ohne Anhänge fatal).
  const handleCancel = async (mail: ScheduledFinalizationEmail) => {
    setCancellingIds(prev => new Set(prev).add(mail.id));
    setError(null);

    try {
      let deleteQuery = supabase
        .from('scheduled_finalization_emails')
        .delete()
        .eq('id', mail.id)
        .eq('versand_status', mail.versand_status);

      if (mail.versand_status === 'geplant') {
        // Claim-Guard: nur solange der Cron die Mail nicht aufgenommen hat
        deleteQuery = deleteQuery.is('versand_claimed_at', null);
      }

      const { data: deleted, error: deleteError } = await deleteQuery.select('id');

      if (deleteError) {
        setError(`Abbrechen fehlgeschlagen: ${deleteError.message}`);
        return;
      }

      if (!deleted || deleted.length === 0) {
        setError('Versand läuft bereits oder ist abgeschlossen — Abbrechen nicht mehr möglich. Die Liste wird beim nächsten Laden der Seite aktualisiert.');
        return;
      }

      // Storage-Cleanup (best-effort)
      const paths = (mail.attachments || []).map(a => a.storage_path);
      if (paths.length > 0) {
        const { error: removeError } = await supabase.storage
          .from(ATTACHMENT_BUCKET)
          .remove(paths);
        if (removeError) {
          console.warn('[ScheduledFinalizationEmails] Storage-Cleanup fehlgeschlagen (non-blocking):', removeError.message);
        }
      }

      setEmails(prev => prev.filter(e => e.id !== mail.id));
      setExpandedIds(prev => {
        const next = new Set(prev);
        next.delete(mail.id);
        return next;
      });
      setConfirmCancelId(null);
    } catch (err) {
      console.error('[ScheduledFinalizationEmails] Abbrechen fehlgeschlagen:', err);
      setError('Abbrechen fehlgeschlagen.');
    } finally {
      setCancellingIds(prev => {
        const next = new Set(prev);
        next.delete(mail.id);
        return next;
      });
    }
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6B7280',
    width: '90px',
    flexShrink: 0
  };

  const valueStyle: React.CSSProperties = {
    fontSize: '13px',
    color: '#111827'
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '8px'
      }}>
        <Clock size={16} color="#374151" />
        <p style={{ fontSize: '14px', fontWeight: 600, color: '#111827', margin: 0 }}>
          Geplante Mandantenmails ({emails.length})
        </p>
      </div>

      {error && (
        <div style={{
          marginBottom: '8px',
          padding: '10px 12px',
          backgroundColor: '#FEF2F2',
          borderRadius: '6px',
          border: '1px solid #FECACA',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={15} color="#DC2626" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: '#DC2626' }}>{error}</span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {emails.map((mail) => {
          const isFailed = mail.versand_status === 'fehler';
          const busy = cancellingIds.has(mail.id);
          const isExpanded = expandedIds.has(mail.id);
          const sendenAls = mail.sender_identity_name || mail.sender_identity_email || mail.erstellt_von_name;
          const attachments = Array.isArray(mail.attachments) ? mail.attachments : [];
          const borderColor = isFailed ? '#FECACA' : '#E5E7EB';

          return (
            <div key={mail.id} style={{
              padding: '10px 12px',
              backgroundColor: isFailed ? '#FEF2F2' : '#F9FAFB',
              borderRadius: '8px',
              border: `1px solid ${borderColor}`
            }}>
              {/* Kompakte Kopfzeile: Betreff, Empfänger, Zeitpunkt + Abbrechen/Löschen.
                  Klick auf die Zeile klappt die Detailansicht auf bzw. zu. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  onClick={() => toggleExpanded(mail.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1,
                    minWidth: 0,
                    cursor: 'pointer'
                  }}
                >
                  {isExpanded
                    ? <ChevronDown size={15} color="#6B7280" style={{ flexShrink: 0 }} />
                    : <ChevronRight size={15} color="#6B7280" style={{ flexShrink: 0 }} />}

                  {isFailed
                    ? <AlertCircle size={15} color="#B91C1C" style={{ flexShrink: 0 }} />
                    : <Clock size={15} color="#6B7280" style={{ flexShrink: 0 }} />}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#111827',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {mail.subject}
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {isFailed && (
                        <span style={{ color: '#B91C1C', fontWeight: 500 }}>Fehlgeschlagen · </span>
                      )}
                      {mail.to_email} · {formatBerlinDateTime(mail.geplanter_versand_am)} Uhr
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setConfirmCancelId(confirmCancelId === mail.id ? null : mail.id)}
                  disabled={busy}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    backgroundColor: '#FFFFFF',
                    border: `1px solid ${borderColor}`,
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: isFailed ? '#B91C1C' : '#4B5563',
                    cursor: busy ? 'not-allowed' : 'pointer',
                    flexShrink: 0
                  }}
                >
                  {isFailed ? <Trash2 size={13} /> : <X size={13} />}
                  {isFailed ? 'Löschen' : 'Abbrechen'}
                </button>
              </div>

              {/* Detailansicht (aufgeklappt) */}
              {isExpanded && (
                <div style={{ marginTop: '12px' }}>
                  {/* Label-Block im Ticket-Wording */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex' }}>
                      <span style={labelStyle}>An:</span>
                      <span style={valueStyle}>{mail.to_email}{mail.cc_email ? `, CC: ${mail.cc_email}` : ''}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={labelStyle}>Zeitpunkt:</span>
                      <span style={valueStyle}>{formatBerlinDateTime(mail.geplanter_versand_am)} Uhr</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={labelStyle}>Von:</span>
                      <span style={valueStyle}>{mail.erstellt_von_name}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={labelStyle}>Senden als:</span>
                      <span style={valueStyle}>{sendenAls}</span>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={labelStyle}>Betreff:</span>
                      <span style={{ ...valueStyle, fontWeight: 600 }}>{mail.subject}</span>
                    </div>
                  </div>

                  {/* Fehlermeldung bei fehlgeschlagenem Versand */}
                  {isFailed && (
                    <p style={{
                      fontSize: '12px',
                      color: '#B91C1C',
                      margin: '0 0 10px 0',
                      lineHeight: '1.4'
                    }}>
                      Versand fehlgeschlagen{mail.fehler_nachricht ? `: ${mail.fehler_nachricht}` : ''} — der Monat ist bereits finalisiert; der Versand muss manuell neu ausgelöst werden.
                    </p>
                  )}

                  {/* Vollständiger eingefrorener Mail-Inhalt — exakt das, was der
                      Cron versendet. Sandbox-iframe: keine Skripte, kein CSS-Bleed. */}
                  <iframe
                    title={`Geplante Mandantenmail ${mail.subject}`}
                    sandbox=""
                    srcDoc={mail.body_html}
                    style={{
                      width: '100%',
                      height: '340px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      backgroundColor: '#FFFFFF',
                      marginBottom: attachments.length > 0 ? '10px' : 0
                    }}
                  />

                  {/* Anhänge mit Download */}
                  {attachments.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {attachments.map((att) => (
                        <div key={att.storage_path} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 10px',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '6px',
                          border: `1px solid ${borderColor}`
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{
                              fontSize: '13px',
                              fontWeight: 500,
                              color: '#374151',
                              margin: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {att.filename}
                            </p>
                            <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>
                              {formatFileSize(att.size_bytes)}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDownload(att)}
                            disabled={downloadingPaths.has(att.storage_path)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 10px',
                              backgroundColor: downloadingPaths.has(att.storage_path) ? '#E5E7EB' : '#EFF6FF',
                              color: downloadingPaths.has(att.storage_path) ? '#9CA3AF' : '#2563EB',
                              border: `1px solid ${downloadingPaths.has(att.storage_path) ? '#D1D5DB' : '#BFDBFE'}`,
                              borderRadius: '4px',
                              cursor: downloadingPaths.has(att.storage_path) ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              fontWeight: 500,
                              flexShrink: 0,
                              marginLeft: '8px'
                            }}
                          >
                            <Download size={12} />
                            {downloadingPaths.has(att.storage_path) ? '...' : 'Download'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Bestätigungsdialog: Abbrechen (geplant) bzw. Löschen (fehler).
                  Bewusst außerhalb der Detailansicht, da der Button in der
                  kompakten Kopfzeile sitzt. */}
              {confirmCancelId === mail.id && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px 12px',
                  backgroundColor: '#FFFFFF',
                  border: `1px solid ${borderColor}`,
                  borderRadius: '6px'
                }}>
                  <p style={{
                    margin: '0 0 10px 0',
                    fontSize: '13px',
                    color: isFailed ? '#991B1B' : '#374151',
                    fontWeight: 500,
                    lineHeight: '1.4'
                  }}>
                    {isFailed
                      ? 'Diese fehlgeschlagene Mandantenmail unwiderruflich löschen? Der Monat bleibt finalisiert; ein Versand müsste manuell neu ausgelöst werden.'
                      : 'Geplante Mandantenmail abbrechen? Nur die Mail wird verworfen — der Monat bleibt finalisiert (inklusive Rechnungsentwurf). Ein Versand müsste manuell neu ausgelöst werden.'}
                  </p>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setConfirmCancelId(null)}
                      disabled={busy}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        fontSize: '13px',
                        fontWeight: 500,
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Zurück
                    </button>
                    <button
                      onClick={() => handleCancel(mail)}
                      disabled={busy}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: busy ? '#9CA3AF' : '#DC2626',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: 500,
                        border: 'none',
                        borderRadius: '4px',
                        cursor: busy ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {busy
                        ? (isFailed ? 'Wird gelöscht...' : 'Wird abgebrochen...')
                        : (isFailed ? 'Endgültig löschen' : 'Mail verwerfen')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScheduledFinalizationEmails;
